import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import getBlogPost from 'src/queries/getBlogPost.graphql';
import camelcaseKeys from 'camelcase-keys';
import Bugsnag from '@bugsnag/js';
import { BlogPostLoading } from 'src/components/Blog/BlogPostLoading';
import { BlogPost as BlogPostType } from 'src/types/global-types';
import { RelatedBlogProducts } from 'src/components/Blog/RelatedBlogProducts';
import { useDeps } from 'src/hooks/useDeps';
import { currentUrl } from 'src/util/currentUrl';
import { resolveUrl } from 'src/queries/__generated__/resolveUrl';
import { useHeadTags, escapeAttr } from 'src/core/engine/head';
import { usePathname } from 'src/hooks/router-hooks';
import { SafeHtml } from 'src/components/SafeHtml/SafeHtml';

import urlResolver from '../../queries/urlResolver.graphql';

import classes from './BlogPost.scss';

export const BlogPostInner: React.FC = () => {
    // Redux pathname tracks AsyncLoader history events even when RR context lags.
    const pathname = usePathname();
    const { DOMAIN, SSR_GQL } = useDeps().env;
    const { setHead } = useHeadTags();

    const {
        data: resolverData,
        error: resolverError,
        loading: resolverLoading,
    } = useQuery<resolveUrl>(urlResolver, {
        ssr: SSR_GQL,
        skip: !pathname,
        variables: { urlKey: pathname },
    });

    const postId = resolverData?.urlResolver?.id;

    const { error, data, loading } = useQuery(getBlogPost, {
        ssr: SSR_GQL,
        skip: postId == null,
        variables: {
            postId: postId != null ? String(postId) : undefined,
        },
    });

    useEffect(() => {
        if (!data?.blogPost) return;
        const { blogPost } = camelcaseKeys(data, { deep: true }) as BlogPostType;
        const title = blogPost.metaTitle ? `<title>${escapeAttr(blogPost.metaTitle)}</title>` : '';
        const meta = blogPost.metaDescription
            ? `<meta name="description" content="${escapeAttr(blogPost.metaDescription)}" />`
            : '';
        const canonicalUrl = DOMAIN ? currentUrl(DOMAIN, pathname) : undefined;
        const link = canonicalUrl ? `<link rel="canonical" href="${escapeAttr(canonicalUrl)}" />` : undefined;
        setHead({ title, meta: meta || undefined, link });
    }, [data, setHead, DOMAIN, pathname]);

    if (resolverError) {
        Bugsnag.notify(`Error: ${resolverError}`);
        return null;
    }

    if (error) {
        Bugsnag.notify(`Error: ${error}`);
        return null;
    }

    if (resolverLoading || loading || postId == null) return <BlogPostLoading />;

    if (!data) {
        return <p>Something went wrong</p>;
    }

    const { blogPost } = camelcaseKeys(data, { deep: true }) as BlogPostType;

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <h1 className={classes.title}>{blogPost.title}</h1>
                <p className={classes.date}>{blogPost.creationTime}</p>
            </div>
            <SafeHtml html={blogPost.filteredContent} />
            {blogPost.relatedProducts && <RelatedBlogProducts relatedProducts={blogPost.relatedProducts} />}
        </div>
    );
};

export const BlogPost: React.FC = () => <BlogPostInner />;
